<template>
  <component
    :is="is"
    :class="[componentClass, $attrs.class]"
    :to="to"
    :type="computedType"
    :disabled="isButtonElement ? isDisabled : undefined"
    :aria-busy="isBusy ? 'true' : undefined"
    :aria-disabled="!isButtonElement && isDisabled ? 'true' : undefined"
    :aria-pressed="ariaPressed"
    v-bind="href == null ? rootAttrs : { ...rootAttrs, href }"
    @click="handleClick"
  >
    <Icon
      v-if="isBusy"
      :path="iconLoading"
      :w="resolvedIconW"
      :h="resolvedIconH"
      :size="resolvedIconSize"
      :class="['animate-spin', leadingIconClass]"
    />
    <Icon
      v-else-if="icon"
      :path="icon"
      :w="resolvedIconW"
      :h="resolvedIconH"
      :size="resolvedIconSize"
      :class="leadingIconClass"
    />
    <span v-if="!isIconOnly && label != null && label !== ''">{{ label }}</span>
    <slot v-else />
    <AppBadge v-if="pillText" :label="pillText" :tone="pillTone" />
    <Icon
      v-if="iconRight"
      :path="iconRight"
      :w="resolvedIconW"
      :h="resolvedIconH"
      :size="resolvedIconSize"
      :class="trailingIconClass"
    />
    <Tip v-if="tip" :tip="tip" :left="tipX === 'left'" :right="tipX === 'right'" :top="tipTop" />
  </component>
</template>

<script>
import { computed, useAttrs } from 'vue'
import { mdiLoading } from '@mdi/js'
import Icon from '@/components/Icon'
import AppBadge from '@/components/ui/feedback/AppBadge.vue'
import Tip from '@/components/ui/feedback/Tip'
import { useComponentElement } from '@/composables/useComponentElement'

// Maps the legacy `pillType` colour names (passed through `useTypeStyles`
// in the old Pill) onto the semantic AppBadge tones.
const pillToneAliases = {
  success: 'success',
  emerald: 'success',
  primary: 'brand',
  danger: 'danger',
  warning: 'warning',
  info: 'info',
  azure: 'info',
  blue: 'info',
  indigo: 'info',
  neutral: 'neutral',
}

const legacyToneAliases = {
  success: 'primary',
  emerald: 'primary',
  primary: 'primary',
  azure: 'info',
  info: 'info',
  blue: 'info',
  danger: 'danger',
  warning: 'warning',
  white: 'neutral',
  light: 'muted',
  indigo: 'info',
  neutral: 'neutral',
  muted: 'muted',
}

const solidToneClasses = {
  primary: [
    'border-theme-brand',
    'bg-theme-brand',
    'text-theme-text-inverse',
    'hover:border-theme-brand-hover',
    'hover:bg-theme-brand-hover',
  ],
  info: [
    'border-theme-info',
    'bg-theme-info',
    'text-theme-text-inverse',
    'hover:border-theme-info/90',
    'hover:bg-theme-info/90',
  ],
  danger: [
    'border-theme-danger',
    'bg-theme-danger',
    'text-theme-text-inverse',
    'hover:border-theme-danger/90',
    'hover:bg-theme-danger/90',
  ],
  warning: [
    'border-theme-warning',
    'bg-theme-warning',
    'text-theme-text-inverse',
    'hover:border-theme-warning/90',
    'hover:bg-theme-warning/90',
  ],
  neutral: [
    'border-theme-border',
    'bg-theme-surface',
    'text-theme-text',
    'hover:border-theme-border-strong',
    'hover:bg-theme-surface-raised',
  ],
  muted: [
    'border-theme-border/60',
    'bg-theme-bg-subtle',
    'text-theme-text-secondary',
    'hover:border-theme-border',
    'hover:bg-theme-surface-raised',
  ],
}

const outlineToneClasses = {
  primary: [
    'border-theme-brand',
    'bg-theme-surface',
    'text-theme-brand',
    'hover:bg-theme-brand-soft',
  ],
  info: [
    'border-theme-info',
    'bg-theme-surface',
    'text-theme-info',
    'hover:bg-theme-info/10',
  ],
  danger: [
    'border-theme-danger',
    'bg-theme-surface',
    'text-theme-danger',
    'hover:bg-theme-danger/10',
  ],
  warning: [
    'border-theme-warning',
    'bg-theme-surface',
    'text-theme-warning',
    'hover:bg-theme-warning/10',
  ],
  neutral: [
    'border-theme-border',
    'bg-theme-surface',
    'text-theme-text-secondary',
    'hover:border-theme-border-strong',
    'hover:bg-theme-surface-raised',
  ],
  muted: [
    'border-theme-border/70',
    'bg-theme-surface',
    'text-theme-text-secondary',
    'hover:bg-theme-bg-subtle',
  ],
}

const softToneClasses = {
  primary: [
    'border-theme-brand/20',
    'bg-theme-brand/10',
    'text-theme-brand',
    'hover:border-theme-brand/30',
    'hover:bg-theme-brand/15',
  ],
  info: [
    'border-theme-info/20',
    'bg-theme-info/10',
    'text-theme-info',
    'hover:border-theme-info/30',
    'hover:bg-theme-info/15',
  ],
  danger: [
    'border-theme-danger/20',
    'bg-theme-danger/10',
    'text-theme-danger',
    'hover:border-theme-danger/30',
    'hover:bg-theme-danger/15',
  ],
  warning: [
    'border-theme-warning/20',
    'bg-theme-warning/10',
    'text-theme-warning',
    'hover:border-theme-warning/30',
    'hover:bg-theme-warning/15',
  ],
  neutral: [
    'border-theme-border/60',
    'bg-theme-surface-raised',
    'text-theme-text-secondary',
    'hover:border-theme-border',
    'hover:bg-theme-surface',
  ],
  muted: [
    'border-transparent',
    'bg-theme-bg-subtle',
    'text-theme-text-secondary',
    'hover:bg-theme-surface-raised',
  ],
}

const ghostToneClasses = {
  primary: [
    'border-transparent',
    'bg-transparent',
    'text-theme-brand',
    'hover:bg-theme-brand/10',
  ],
  info: [
    'border-transparent',
    'bg-transparent',
    'text-theme-info',
    'hover:bg-theme-info/10',
  ],
  danger: [
    'border-transparent',
    'bg-transparent',
    'text-theme-danger',
    'hover:bg-theme-danger/10',
  ],
  warning: [
    'border-transparent',
    'bg-transparent',
    'text-theme-warning',
    'hover:bg-theme-warning/10',
  ],
  neutral: [
    'border-transparent',
    'bg-transparent',
    'text-theme-text-secondary',
    'hover:bg-theme-surface-raised',
  ],
  muted: [
    'border-transparent',
    'bg-transparent',
    'text-theme-text-muted',
    'hover:bg-theme-surface-raised',
  ],
}

const linkToneClasses = {
  primary: [
    'border-transparent',
    'bg-transparent',
    'text-theme-brand',
    'hover:text-theme-brand-hover',
  ],
  info: [
    'border-transparent',
    'bg-transparent',
    'text-theme-info',
    'hover:text-theme-info/80',
  ],
  danger: [
    'border-transparent',
    'bg-transparent',
    'text-theme-danger',
    'hover:text-theme-danger/80',
  ],
  warning: [
    'border-transparent',
    'bg-transparent',
    'text-theme-warning',
    'hover:text-theme-warning/80',
  ],
  neutral: [
    'border-transparent',
    'bg-transparent',
    'text-theme-text-secondary',
    'hover:text-theme-text',
  ],
  muted: [
    'border-transparent',
    'bg-transparent',
    'text-theme-text-muted',
    'hover:text-theme-text-secondary',
  ],
}

const sizeClasses = {
  xs: {
    text: ['h-control-xs', 'px-control-x-xs', 'py-control-y-xs', 'text-xs'],
    icon: ['h-control-xs', 'w-control-xs', 'px-0', 'py-0'],
  },
  sm: {
    text: ['h-control-sm', 'px-control-x-sm', 'py-control-y-sm', 'text-sm'],
    icon: ['h-control-sm', 'w-control-sm', 'px-0', 'py-0'],
  },
  md: {
    text: ['h-control-md', 'px-control-x-md', 'py-control-y-md', 'text-sm'],
    icon: ['h-control-md', 'w-control-md', 'px-0', 'py-0'],
  },
  lg: {
    text: ['h-control-lg', 'px-control-x-lg', 'py-control-y-lg', 'text-sm'],
    icon: ['h-control-lg', 'w-control-lg', 'px-0', 'py-0'],
  },
}

const justifyClasses = {
  start: 'justify-start',
  center: 'justify-center',
  between: 'justify-between',
}

// Icons must scale with the button size — Icon's default renders a 16px glyph
// inside a fixed 24px (w-6) box, leaving dead space on the icon side that
// throws the optical left/right balance off. We therefore (a) size the glyph
// proportionally to the button and (b) tighten the icon BOX to that same size
// so the glyph hugs the label instead of floating in a 24px square. Icon-only
// buttons keep the default box (the glyph is the whole control).
const iconSizeByButtonSize = { xs: 14, sm: 16, md: 18, lg: 20 }
const iconOnlySizeByButtonSize = { xs: 16, sm: 18, md: 20, lg: 22 }
// Static class strings (not interpolated) so Tailwind's JIT scanner emits them.
const iconBoxWByButtonSize = { xs: 'w-3.5', sm: 'w-4', md: 'w-[18px]', lg: 'w-5' }
const iconBoxHByButtonSize = { xs: 'h-3.5', sm: 'h-4', md: 'h-[18px]', lg: 'h-5' }

function resolveVariant(props) {
  if (props.variant) {
    return props.variant
  }

  if (props.outline) {
    return 'outline'
  }

  return 'solid'
}

function resolveTone(props) {
  return legacyToneAliases[props.tone || props.color] || 'neutral'
}

function resolveSize(props) {
  if (props.size) {
    return props.size
  }

  return props.small ? 'sm' : 'md'
}

function resolveVariantClasses(variant, tone) {
  switch (variant) {
    case 'outline':
      return outlineToneClasses[tone] || outlineToneClasses.neutral
    case 'soft':
      return softToneClasses[tone] || softToneClasses.neutral
    case 'ghost':
      return ghostToneClasses[tone] || ghostToneClasses.neutral
    case 'link':
      return linkToneClasses[tone] || linkToneClasses.neutral
    default:
      return solidToneClasses[tone] || solidToneClasses.neutral
  }
}

export default {
  name: 'AppButton',
  inheritAttrs: false,
  components: {
    Icon,
    Tip,
    AppBadge,
  },
  props: {
    label: [String, Number],
    icon: String,
    loading: Boolean,
    isLoading: Boolean,
    disabled: Boolean,
    iconRight: String,
    iconW: String,
    iconH: String,
    iconSize: [String, Number],
    href: String,
    to: [String, Object],
    type: String,
    iconLoading: {
      type: String,
      default: mdiLoading,
    },
    color: {
      type: String,
      default: 'white',
    },
    tone: {
      type: String,
      default: '',
    },
    variant: {
      type: String,
      default: '',
    },
    size: {
      type: String,
      default: '',
    },
    small: Boolean,
    outline: Boolean,
    block: Boolean,
    iconOnly: Boolean,
    justify: {
      type: String,
      default: 'center',
    },
    active: Boolean,
    activeSoft: Boolean,
    noBorder: Boolean,
    noFocusRing: Boolean,
    as: String,
    tip: String,
    tipX: String,
    tipTop: Boolean,
    roundedFull: Boolean,
    addon: Boolean,
    pillText: String,
    pillType: {
      type: String,
      default: 'info',
    },
  },
  emits: ['click'],
  setup(props, { emit }) {
    const attrs = useAttrs()
    const { is, computedType } = useComponentElement(props)

    const isBusy = computed(() => props.loading || props.isLoading)
    const isDisabled = computed(() => props.disabled || isBusy.value)
    const isButtonElement = computed(() => is.value === 'button')
    const variant = computed(() => resolveVariant(props))
    const tone = computed(() => resolveTone(props))
    const size = computed(() => resolveSize(props))
    const isIconOnly = computed(
      () => props.iconOnly || (props.icon && !props.label && !props.iconRight && !attrs.title)
    )

    // Explicit iconSize/iconW/iconH always win; otherwise derive a size that
    // scales with the button so icons stay proportional to the label.
    const resolvedIconSize = computed(() => {
      if (props.iconSize != null && props.iconSize !== '') return props.iconSize
      if (props.iconW || props.iconH) return undefined
      const map = isIconOnly.value ? iconOnlySizeByButtonSize : iconSizeByButtonSize
      return map[size.value] ?? map.md
    })

    // Tighten the icon box to the glyph (text+icon buttons only). Explicit
    // iconW/iconH still win; icon-only buttons keep Icon's default 24px box.
    const resolvedIconW = computed(() => {
      if (props.iconW) return props.iconW
      if (isIconOnly.value) return undefined
      return iconBoxWByButtonSize[size.value] ?? iconBoxWByButtonSize.md
    })
    const resolvedIconH = computed(() => {
      if (props.iconH) return props.iconH
      if (isIconOnly.value) return undefined
      return iconBoxHByButtonSize[size.value] ?? iconBoxHByButtonSize.md
    })

    // An icon glyph carries internal whitespace inside its viewBox, so a
    // leading icon sits optically further from the edge than the trailing text
    // does. A small negative margin (the standard Tailwind-UI idiom) pulls the
    // glyph back so edge→icon visually matches text→edge. Only applied when a
    // text label sits beside the icon — never on icon-only buttons.
    const hasLabelText = computed(() => props.label != null && props.label !== '')
    const leadingIconClass = computed(() =>
      !isIconOnly.value && hasLabelText.value ? 'shrink-0 -ml-0.5' : 'shrink-0'
    )
    const trailingIconClass = computed(() =>
      hasLabelText.value ? 'shrink-0 -mr-0.5' : 'shrink-0'
    )

    const rootAttrs = computed(() => {
      const { class: attrClass, tabindex, ...rest } = attrs

      if (!isButtonElement.value && isDisabled.value) {
        rest.tabindex = -1
      } else if (tabindex != null) {
        rest.tabindex = tabindex
      }

      return rest
    })

    const ariaPressed = computed(() => (props.active ? 'true' : undefined))

    const componentClass = computed(() => {
      const classes = [
        'app-button-theme',
        `app-button-theme--variant-${variant.value}`,
        `app-button-theme--tone-${tone.value}`,
        `app-button-theme--size-${size.value}`,
        `app-button-theme--justify-${props.justify}`,
        'inline-flex',
        'items-center',
        'gap-2',
        'border',
        'font-medium',
        'transition-colors',
        'duration-150',
        'select-none',
        'whitespace-nowrap',
        justifyClasses[props.justify] || justifyClasses.center,
        resolveVariantClasses(variant.value, tone.value),
      ]

      if (variant.value === 'link') {
        classes.push('px-0', 'py-0', 'min-h-0', 'underline-offset-4')
      } else {
        classes.push(...(sizeClasses[size.value]?.[isIconOnly.value ? 'icon' : 'text'] || sizeClasses.md.text))
      }

      if (isIconOnly.value) {
        classes.push('app-button-theme--icon-only')
      }

      if (props.block) {
        classes.push('app-button-theme--block')
        classes.push('w-full')
      }

      if (props.noFocusRing || variant.value === 'link') {
        classes.push('app-button-theme--no-focus')
        classes.push('focus:outline-none')
      } else {
        classes.push('app-button-theme--focusable')
        classes.push(
          'focus:outline-none',
          'focus:shadow-theme-focus'
        )
      }

      if (props.noBorder || variant.value === 'link' || variant.value === 'ghost') {
        classes.push('border-transparent')
      }

      if (props.active) {
        classes.push('app-button-theme--active')
        classes.push('ring-2', 'ring-theme-focus/35')
      } else if (props.activeSoft) {
        classes.push('app-button-theme--active-soft')
        classes.push('ring-1', 'ring-theme-border/80')
      }

      if (props.addon) {
        classes.push(
          props.roundedFull
            ? 'rounded-none first:rounded-l-theme-pill last:rounded-r-theme-pill'
            : 'rounded-none first:rounded-l-theme-lg last:rounded-r-theme-lg'
        )
      } else {
        if (props.roundedFull) {
          classes.push('app-button-theme--rounded-full')
        }
        // Unified control radius — all controls share --radius-lg (theme-lg).
        classes.push(props.roundedFull ? 'rounded-theme-pill' : 'rounded-theme-lg')
      }

      if (isDisabled.value) {
        classes.push('app-button-theme--disabled')
        classes.push('cursor-not-allowed', 'opacity-60')
      } else {
        classes.push('cursor-pointer')
      }

      return classes
    })

    // Map the legacy `pillType` colour names onto AppBadge tones.
    const pillTone = computed(() => pillToneAliases[props.pillType] || 'neutral')

    const handleClick = (event) => {
      if (isDisabled.value) {
        event.preventDefault()
        event.stopPropagation()
        return
      }

      // Safari touch leaves the previous field focused; dialogs need the actual opener.
      if (isButtonElement.value && event.detail > 0) event.currentTarget.focus({ preventScroll: true })
      emit('click', event)
    }

    return {
      is,
      computedType,
      isBusy,
      isDisabled,
      isButtonElement,
      rootAttrs,
      ariaPressed,
      componentClass,
      isIconOnly,
      resolvedIconSize,
      resolvedIconW,
      resolvedIconH,
      leadingIconClass,
      trailingIconClass,
      pillTone,
      handleClick,
    }
  },
}
</script>
